
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { ArrowLeft } from 'lucide-react';
import { 
  ChartContainer, 
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

interface ContinentData {
  name: string;
  count: number;
}

function mapAnswerToContinent(answer: string): string {
  if (answer.includes('Europe') || answer.includes('UK')) {
    return 'Europe';
  } else if (answer.includes('North America') || answer.includes('USA') || answer.includes('Canada')) {
    return 'North America';
  } else if (answer.includes('Asia') || answer.includes('Japan') || answer.includes('China') || answer.includes('Singapore')) {
    return 'Asia';
  } else if (answer.includes('Australia') || answer.includes('New Zealand')) {
    return 'Australia & NZ';
  } else if (answer.includes('Middle East')) {
    return 'Middle East';
  } else if (answer.includes('Africa')) {
    return 'Africa';
  } else {
    return 'Other';
  }
}

const StudyAbroadInsights = () => {
  const [loading, setLoading] = useState(true);
  const [continentData, setContinentData] = useState<ContinentData[]>([]);

  useEffect(() => {
    const fetchStudyAbroadData = async () => {
      setLoading(true);
      try {
        // Fetch responses where question_id = 63 and answer is not "Not Interested"
        const { data, error } = await supabase
          .from('user_responses')
          .select('answer')
          .eq('question_id', 63)
          .neq('answer', 'Not Interested');

        if (error) throw error;

        // Count by continent
        const continentCounts: Record<string, number> = {};
        data?.forEach(response => {
          const continent = mapAnswerToContinent(response.answer);
          continentCounts[continent] = (continentCounts[continent] || 0) + 1;
        });

        // Convert to array format for display and chart
        const formattedData = Object.entries(continentCounts).map(([name, count]) => ({
          name,
          count
        })).sort((a, b) => b.count - a.count); // Sort by count descending

        setContinentData(formattedData);
      } catch (error) {
        console.error('Error fetching study abroad data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudyAbroadData();
  }, []);

  // Chart config for the Bar Chart
  const chartConfig = {
    count: {
      label: 'Count',
      color: '#8B5CF6' // Purple color for the chart bars
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Study Abroad Insights | Admin | Career Guide Kerala</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/administrator/dashboard">
              <Button variant="ghost" size="sm" className="flex items-center gap-1 mb-4">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold tracking-tight">Study Abroad Insights</h1>
            <p className="text-gray-500 mt-1">Analysis of student preferences for studying abroad</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center my-12">
            <p>Loading study abroad data...</p>
          </div>
        ) : (
          <>
            {/* Grid of continent counts */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {continentData.map((item, index) => (
                <Card key={index} className="bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-medium">{item.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{item.count}</div>
                    <div className="text-sm text-muted-foreground">Students interested</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Bar Chart */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>Study Abroad Interest by Region</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ChartContainer config={chartConfig}>
                    <BarChart data={continentData}>
                      <XAxis
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                      />
                      <Bar
                        dataKey="count"
                        fill="var(--color-count, #8B5CF6)"
                        radius={[4, 4, 0, 0]}
                      />
                      <ChartTooltip
                        content={<ChartTooltipContent />}
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                    </BarChart>
                  </ChartContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default StudyAbroadInsights;
